import { type FC, useState } from 'react';

import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import { EyeIcon } from '@heroicons/react/24/outline';
import { CheckIcon, ChevronDownIcon, LockOpenIcon } from '@heroicons/react/24/solid';
import cn from 'classnames';

import type { ComposerDestination } from '../../CommunityPostComposer.type';
import { destinationKey } from '../../destinationKey';
import { POPOVER_PANEL_CLASSES, tr } from '../constants';

type ComposerSpaceSelectorProps = {
  destinations: ComposerDestination[];
  selected: ComposerDestination;
  onChange: (destination: ComposerDestination) => void;
  translations?: Record<string, string>;
  disabled?: boolean;
};

const destinationLabel = (destination: ComposerDestination, translations?: Record<string, string>): string => {
  if (destination.kind === 'feed') return tr(translations, 'community.composer.destination_feed');
  return destination.space.name;
};

const ReadOnlyHint: FC<{ hint: string }> = ({ hint }) => {
  const [open, setOpen] = useState(false);
  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'top-end',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
  });
  const interactions = useInteractions([useHover(floating.context), useDismiss(floating.context)]);

  return (
    <>
      <span
        ref={floating.refs.setReference}
        className="ml-auto inline-flex shrink-0 cursor-help text-black/40"
        {...interactions.getReferenceProps()}
      >
        <EyeIcon className="size-3.5" aria-hidden="true" />
        <span className="sr-only">{hint}</span>
      </span>
      {open && (
        <FloatingPortal>
          <div
            ref={floating.refs.setFloating}
            style={floating.floatingStyles}
            className="z-[110] max-w-[240px] rounded bg-black px-2 py-1 text-xs text-white"
            {...interactions.getFloatingProps()}
          >
            {hint}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export const ComposerSpaceSelector: FC<ComposerSpaceSelectorProps> = ({
  destinations,
  selected,
  onChange,
  translations,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const selectedKey = destinationKey(selected);
  const selectedLabel = destinationLabel(selected, translations);
  const readOnlyHint = tr(translations, 'community.action_menu.move_post_read_only_hint');

  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const interactions = useInteractions([useClick(floating.context), useDismiss(floating.context)]);

  return (
    <>
      <button
        ref={disabled ? undefined : floating.refs.setReference}
        type="button"
        disabled={disabled}
        className={cn(
          'flex items-center gap-2 h-8 px-3 rounded-full border border-black/10 text-[13px] font-medium leading-4 tracking-[-0.13px] text-black transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          { 'hover:bg-black/5': !disabled },
        )}
        {...(disabled ? {} : interactions.getReferenceProps())}
        aria-label={tr(translations, 'community.composer.choose_destination')}
        aria-expanded={disabled ? undefined : open}
      >
        <span className="truncate max-w-[200px]">{selectedLabel}</span>
        {selected.kind === 'channel' && selected.space.gated && (
          <LockOpenIcon className="size-3.5 shrink-0 text-black/40" aria-hidden="true" />
        )}
        <ChevronDownIcon className="size-4 text-black/50" aria-hidden="true" />
      </button>
      {open && !disabled && (
        <FloatingPortal>
          <FloatingFocusManager context={floating.context} modal={false}>
            <div
              ref={floating.refs.setFloating}
              style={floating.floatingStyles}
              className={cn(
                POPOVER_PANEL_CLASSES,
                'z-[100] max-h-60 w-56 overflow-auto rounded-2xl py-1 text-sm focus:outline-none',
              )}
              {...interactions.getFloatingProps()}
            >
              {destinations.map((destination) => {
                const key = destinationKey(destination);
                const isSelected = key === selectedKey;
                const label = destinationLabel(destination, translations);
                const isDisabled = destination.kind === 'channel' && destination.space.disabled === true;
                return (
                  <div key={key} className="flex items-center pr-3">
                    <button
                      type="button"
                      disabled={isDisabled}
                      onClick={() => {
                        onChange(destination);
                        setOpen(false);
                      }}
                      className={cn(
                        'flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left select-none disabled:cursor-not-allowed disabled:text-black/40',
                        isDisabled ? 'cursor-not-allowed text-black/40' : 'cursor-default text-black hover:bg-black/5',
                        { 'font-medium': isSelected },
                      )}
                    >
                      {isSelected ? (
                        <CheckIcon className="size-4 shrink-0" aria-hidden="true" />
                      ) : (
                        <span className="size-4 shrink-0" />
                      )}
                      <span className="truncate">{label}</span>
                      {!isDisabled && destination.kind === 'channel' && destination.space.gated && (
                        <LockOpenIcon className="size-3.5 shrink-0 text-black/40" aria-hidden="true" />
                      )}
                    </button>
                    {isDisabled && <ReadOnlyHint hint={readOnlyHint} />}
                  </div>
                );
              })}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};
